import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import {
  selectShareLink,
  setShareLinkState,
} from '@/app/redux/slices/shareLinkSlice';
import { DataLoader } from '@/common/ui/LoadingSpinners';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import IndividualLearnerView from '@/dashboard/LearnerView/IndividualLearnerView';
import api from '@/db/apiClient';
import { Button } from '@shadcn/components/button';
import { Link2Off } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function ShareViewLayout() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { shareLinkId } = useParams();

  const { shareLink } = useAppSelector(selectShareLink);

  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!shareLinkId) return;
      setLoading(true);
      try {
        const data = await api.initShareLink(shareLinkId);
        if (!data) return;
        dispatch(setShareLinkState(data));

        // Scroll to session
        if (data.shareLink.type === 'session') {
          setTimeout(() => {
            const el = document.getElementById(
              `session-${data.shareLink.resourceId}`,
            );
            el?.scrollIntoView({ behavior: 'smooth' });
          }, 500);
        }
      } catch (e) {
        console.error('Error getting share link:', e);
        toast.error('Failed to get share link details', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      api.signOutShareLink().catch(console.error);
    };
  }, [dispatch, shareLinkId]);

  const handleGoBack = () => {
    const navigationStack = JSON.parse(
      sessionStorage.getItem('navigationStack') || '[]',
    );
    navigationStack.pop(); // Remove current path
    const previousPath = navigationStack[navigationStack.length - 1];

    if (previousPath) {
      sessionStorage.setItem(
        'navigationStack',
        JSON.stringify(navigationStack),
      );
      navigate(-1);
    } else {
      navigate(getDefaultDashboardRoute());
    }
  };

  return isLoading ? (
    <div className="h-svh w-full flex items-center justify-center">
      <DataLoader />
    </div>
  ) : shareLink ? (
    <IndividualLearnerView />
  ) : (
    <div className="h-svh w-full flex items-center justify-center">
      <div className="text-center space-y-4 p-8">
        <span className="flex justify-center">
          <Link2Off size={72} className="text-neutral-600" />
        </span>
        <h1 className="text-3xl font-bold text-neutral-900">
          Link invalid or expired
        </h1>
        <p className="text-base text-neutral-600">
          Please contact the person who shared it with you to generate a new
          one.
        </p>
        <div className="py-4">
          <Button onClick={handleGoBack} className="min-w-32">
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
}
